import { create } from 'zustand';

/**
 * Global "a fee change is recalculating P&L" signal. Incremented while a fee-driven recompute is in
 * flight — a broker-fee config change (server re-resolves the affected account + rebuilds) or a
 * manual per-trade / round-trip fee edit (rebuilds that scope) — including the follow-up refetch,
 * and decremented when it settles. A counter (not a bool) so overlapping recalcs don't clear early.
 *
 * The single global "Recalculating P&L…" indicator (Topbar) reads useIsRecalculating(); existing
 * numbers stay visible and refresh when the recompute lands.
 */
interface RecalcState {
  active: number;
  begin: () => void;
  end: () => void;
}

export const useRecalcStore = create<RecalcState>(set => ({
  active: 0,
  begin: () => set(s => ({ active: s.active + 1 })),
  end:   () => set(s => ({ active: Math.max(0, s.active - 1) })),
}));

/** True while one or more fee-driven recalculations are in flight. */
export const useIsRecalculating = () => useRecalcStore(s => s.active > 0);

/** Imperative begin/end for use inside TanStack mutation callbacks (outside React render). */
export const recalc = {
  begin: () => useRecalcStore.getState().begin(),
  end:   () => useRecalcStore.getState().end(),
};
